import {
  GetAgentResponseObj,
  GetListAgentsResponseObj,
  GetSearchSuggestionsResponseObj,
} from "@/types/interfaces/agents";
import { GetSubordinateResponseObj } from "@/types/interfaces/crm";
import axiosInstance from "../axiosInstance";

interface GetAgentArgs {
  email: string;
}

interface GetAgentSubordinatesArgs {
  email: string;
}

interface GetListAgentsArgs {
  type: string;
  selection?: string;
}

export const getAgent = async (args: GetAgentArgs): Promise<GetAgentResponseObj> => {
  const res = await axiosInstance.post(`/admin/agent`, { email: args.email });

  const { message } = res.data;

  return {
    agent: message || {},
  };
};

export const getAgentSubordinates = async (args: GetAgentSubordinatesArgs): Promise<GetSubordinateResponseObj> => {
  const res = await axiosInstance.get(`/admin/subordinate/${encodeURIComponent(args.email)}`);
  return {
    subordinates: res.data?.message || [],
  };
};

export const getListAgents = async (args: GetListAgentsArgs): Promise<GetListAgentsResponseObj> => {
  const searchParams = new URLSearchParams();

  if (args.type) {
    searchParams.append("type", args.type);
  }

  if (args.selection) {
    searchParams.append("selection", args.selection);
  }

  const res = await axiosInstance.get(`/admin/agents/${searchParams.toString() ? `?${searchParams.toString()}` : ""}`);

  const { message } = res.data;

  return {
    agents: message || [],
  };
};

interface LookupAgentsFilters {
  /** Travcom agent numbers, padded or not. */
  agentIds?: string[];
  /** Matched exactly, case included. */
  ttandEmails?: string[];
  /** Fields to return - the API only allows a fixed set. */
  select?: string[];
}

/** Admin only. Every agent matching the filters (keys AND, values within a key OR). */
export const lookupAgents = async <T = Record<string, unknown>>({
  agentIds,
  ttandEmails,
  select,
}: LookupAgentsFilters): Promise<T[]> => {
  const res = await axiosInstance.get("/admin/agents/lookup", {
    params: {
      many: true,
      ...(agentIds?.length && { agentIds: agentIds.join(",") }),
      ...(ttandEmails?.length && { ttandEmails: ttandEmails.join(",") }),
      ...(select?.length && { select: select.join(" ") }),
    },
  });
  return res.data?.message || [];
};

interface LookedUpAgent {
  agentId: number;
  firstname?: string;
  lastname?: string;
  ttandEmail?: string;
  status?: "NEW" | "ACTIVE" | "LEAVING" | "RESIGNED";
}

export interface AgentByAgentNumber {
  /** As requested, e.g. "000123". */
  agentNumber: string;
  /** More than one hub agent carries this number, so none is picked. */
  isAmbiguous: boolean;
  /** Absent when no single hub agent matched. */
  name?: string | null;
  email?: string | null;
  status?: LookedUpAgent["status"] | null;
}

/** One result per travcom agent number - its hub agent, or why there isn't exactly one. */
export const getAgentsByAgentNumbers = async (agentNumbers: string[]): Promise<AgentByAgentNumber[]> => {
  const agents = await lookupAgents<LookedUpAgent>({
    agentIds: agentNumbers,
    select: ["agentId", "firstname", "lastname", "ttandEmail", "status"],
  });

  return agentNumbers.map((agentNumber) => {
    const matches = agents.filter((agent) => agent.agentId === parseInt(agentNumber, 10));
    const [agent] = matches;

    return {
      agentNumber,
      isAmbiguous: matches.length > 1,
      ...(matches.length === 1 && {
        name: [agent.firstname, agent.lastname].filter(Boolean).join(" ") || null,
        email: agent.ttandEmail || null,
        status: agent.status ?? null,
      }),
    };
  });
};

export const getS3Link = async (fileName: string): Promise<string> => {
  const res = await axiosInstance.post(`/agents/s3Read`, { fileName });

  const { message } = res.data;

  return message || "";
};

export interface UpdateCalloutArgs {
  ttandEmail: string;
  founderCallout?: string;
  supportCallout?: string;
  builderCallout?: string;
}

export const updateCallout = async (args: UpdateCalloutArgs): Promise<void> => {
  await axiosInstance.post(`/admin/updateCallout`, args);
};

interface UpdateIdentifierNamesArgs {
  ttandEmail: string;
  identifierNames: string[];
}

/** rejects identifiers already claimed by another agent, and names them in the error */
export const updateIdentifierNames = async ({
  ttandEmail,
  identifierNames,
}: UpdateIdentifierNamesArgs): Promise<void> => {
  await axiosInstance.put("/admin/updateIdentifierNames", { ttandEmail, identifierNames });
};

export const getSearchSuggestions = async (
  query: string,
  signal?: AbortSignal,
): Promise<GetSearchSuggestionsResponseObj> => {
  const res = await axiosInstance.get(`/admin/searchSuggestions-v2?search=${encodeURIComponent(query)}`, { signal });

  const { message } = res.data;

  return { suggestions: message || [] };
};

export interface SendAgentsEmailArgs {
  /** The agents' TTAND emails; the server looks up where to send */
  agentEmails: string[];
  sendTo: "ttandEmail" | "otherEmail";
  subject: string;
  content: string;
  ccSelf: boolean;
}

export interface SendAgentsEmailResult {
  sent: number;
  failed: string[];
  /** Agents without an address of the chosen kind */
  skipped: number;
  from: string;
}

// Sent as the logged-in admin, one email per agent
export const sendAgentsEmail = async (args: SendAgentsEmailArgs): Promise<SendAgentsEmailResult> => {
  const res = await axiosInstance.post(`/admin/agents/send-email`, args);
  return res.data.message;
};
